/**
 * Register the service worker at app startup
 * This is called once when the app loads to ensure SW is available
 * for push notifications and other PWA features
 */

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

// Get the correct SW file path based on environment
function getSwPath(): string {
  // In production, vite-plugin-pwa generates sw.js
  // In development, it uses the module directly
  return '/sw.js';
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isBrowser) {
    console.log('[SW] Not in browser environment');
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service Worker not supported');
    return null;
  }

  const swPath = getSwPath();
  console.log('[SW] Attempting to register SW at:', swPath);

  try {
    // First check if there's an existing registration
    const existingRegistration = await navigator.serviceWorker.getRegistration('/');
    
    if (existingRegistration) {
      console.log('[SW] Found existing registration');
      
      // Check if SW is active
      if (existingRegistration.active) {
        console.log('[SW] Existing SW is active');
        
        // Check for updates
        existingRegistration.update().catch(err => {
          console.warn('[SW] Update check failed:', err);
        });
        
        return existingRegistration;
      }
      
      // If there's a waiting or installing worker, wait for it
      if (existingRegistration.waiting || existingRegistration.installing) {
        console.log('[SW] SW is installing/waiting');
        return existingRegistration;
      }
    }

    // Register new SW
    console.log('[SW] Registering new Service Worker...');
    const registration = await navigator.serviceWorker.register(swPath, {
      scope: '/',
      updateViaCache: 'none',
    });

    console.log('[SW] Service Worker registered successfully');
    console.log('[SW] Scope:', registration.scope);
    console.log('[SW] Active:', !!registration.active);
    console.log('[SW] Waiting:', !!registration.waiting);
    console.log('[SW] Installing:', !!registration.installing);

    // Listen for activation
    if (registration.installing) {
      registration.installing.addEventListener('statechange', (e) => {
        const sw = e.target as ServiceWorker;
        console.log('[SW] State changed:', sw.state);
      });
    }

    return registration;
  } catch (error) {
    console.error('[SW] Service Worker registration failed:', error);
    
    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('MIME type')) {
        console.error('[SW] MIME type error - sw.js may not be generated correctly');
        console.error('[SW] This is expected in development/preview. SW will work in production build.');
      } else if (error.message.includes('SecurityError')) {
        console.error('[SW] Security error - check HTTPS and scope');
      }
    }
    
    return null;
  }
}

/**
 * Get the current service worker registration
 */
export async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isBrowser || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    return await navigator.serviceWorker.getRegistration('/');
  } catch {
    return null;
  }
}

/**
 * Check if service worker is ready and active
 */
export async function isServiceWorkerReady(): Promise<boolean> {
  if (!isBrowser || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    return !!(registration?.active);
  } catch {
    return false;
  }
}

/**
 * Wait for service worker to be ready
 */
export async function waitForServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isBrowser || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    console.log('[SW] Service Worker is ready');
    return registration;
  } catch (error) {
    console.error('[SW] Error waiting for SW:', error);
    return null;
  }
}

// Auto-register on module load (only in browser)
if (isBrowser) {
  // Delay registration slightly to not block initial render
  setTimeout(() => {
    registerServiceWorker();
  }, 1000);
}
